import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_UX, AppUX, environment } from '@y42/environment';
import { ErrorHandlerService } from '@y42/error-handling';
import { OrgUIModel } from '@y42/org/models';
import { isNotNullish } from '@y42/util/nullish';
import { map, Observable, ObservableInput, ObservedValueOf, OperatorFunction, throwError } from 'rxjs';
import {
  AmazonS3StorageConfiguration,
  AzureBlobStorageConfiguration,
  BigQueryGoogleCloudStorageDataWarehouseRequestHttpModel,
  BucketTypeHttpValue,
  DatabaseType,
  DatabaseTypeHttpValue,
  DataWarehouseAmazonS3StorageBucketHttpModel,
  DataWarehouseAzureBlobStorageBucketHttpModel,
  DataWarehouseBigQueryDatabaseHttpModel,
  DataWarehouseBucketHttpModel,
  DataWarehouseConfig,
  DataWarehouseDatabaseHttpModel,
  DataWarehouseGoogleCloudStorageBucketHttpModel,
  DataWarehouseHttpModel,
  DataWarehouseRequestHttpModel,
  DataWarehouseSnowflakeDatabaseHttpModel,
  ErrorResponseHttpModel,
  GitProvider,
  GoogleCloudStorageConfiguration,
  OrgSpaceHttpModel,
  SnowflakeAmazonS3StorageDataWarehouseRequestHttpModel,
  SnowflakeAzureBlobStorageDataWarehouseRequestHttpModel,
  SnowflakeConfiguration,
  SpaceCreateRequestHttpModel,
  SpacesResponseHttpModel,
} from '../models';
import { BucketType } from '../models/bucket-type.enum';
import {
  DataWarehouse,
  DataWarehouseAmazonS3StorageBucket,
  DataWarehouseAzureBlobStorageBucket,
  DataWarehouseBigQueryDatabase,
  DataWarehouseBucket,
  DataWarehouseDatabase,
  DataWarehouseGoogleCloudStorageBucket,
  DataWarehouseSnowflakeDatabase,
  OrgSpaceStoreModel,
  OrgSpaceUpdateSettings,
} from '../models/org-space.model';
import { buildAmazonS3StorageUrl, buildAzureBlobStorageUrl, buildGoogleCloudStorageUrl } from './bucket-http.util';
import { buildBigQueryDatabaseUrl, buildSnowflakeDatabaseUrl } from './database-http.util';

@Injectable({ providedIn: 'root' })
export class OrgSpaceHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly errorHandlerService: ErrorHandlerService,
    @Inject(APP_UX) private readonly appUX: AppUX,
  ) {}

  private readonly baseUrl = environment.url.spaces_api;
  private readonly isUX3 = this.appUX === 'ux3';

  createOrgSpace(
    orgId: OrgUIModel['id'],
    spaceName: string,
    dataWarehouseConfig: DataWarehouseConfig,
  ): Observable<OrgSpaceStoreModel | null> {
    const dataWarehouse = this.mapDataWarehouseConfigToHttpModel(dataWarehouseConfig);

    if (!dataWarehouse) {
      return throwError(
        () => new Error('[OrgSpaceHttpService.createOrgSpace]: invalid data warehouse configuration combination'),
      );
    }

    const request: SpaceCreateRequestHttpModel = {
      space_name: spaceName,
      database: dataWarehouse,
      is_dbt_space: this.isUX3,
    };

    return this.httpClient.post<OrgSpaceHttpModel>(`${this.baseUrl}/company/${orgId}/gitlab/setup/space`, request).pipe(
      map((space) => this.mapSpaceHttpModel(space)),
      this.catchErrorAndThrow('[OrgSpaceHttpService.createOrgSpace]: could not create space'),
    );
  }

  getGitInternalCloneUrl(orgId: OrgUIModel['id'], spaceId: OrgSpaceStoreModel['id']): string {
    return `${this.baseUrl}/company/${orgId}/clone/${spaceId}`;
  }

  getOrgSpaces(orgId: OrgUIModel['id']): Observable<OrgSpaceStoreModel[]> {
    return this.httpClient.get<SpacesResponseHttpModel>(`${this.baseUrl}/company/${orgId}`).pipe(
      map((response) =>
        response.spaces
          .filter(({ is_dbt }) => !is_dbt)
          .map((space) => this.mapSpaceHttpModel(space))
          .filter(isNotNullish),
      ),
      this.catchErrorAndThrow('[OrgSpaceHttpService.getOrgSpaces]: error'),
    );
  }

  deleteOrgSpace(orgId: OrgUIModel['id'], spaceId: OrgSpaceStoreModel['id']): Observable<null> {
    return this.httpClient.delete<unknown>(`${this.baseUrl}/company/${orgId}/${spaceId}`).pipe(
      map(() => null),
      this.catchErrorAndThrow('[OrgSpaceHttpService.deleteOrgSpace]: error'),
    );
  }

  private mapSpaceHttpModel(space: OrgSpaceHttpModel): OrgSpaceStoreModel | null {
    const dataWarehouse = this.mapDataWarehouseModel(space.data_warehouse);

    if (!dataWarehouse) {
      return null;
    }

    const spaceName = (space.space_name ?? space.name)!; // fallback for https://linear.app/y42/issue/PLA-2943/adjust-to-the-space-model-changes

    return {
      dataWarehouse,
      orgId: Number(space.company_id),
      gitProvider: GitProvider.Gitlab,
      gitlabGroupId: space.gitlab_group_id,
      gitlabUserId: space.gitlab_user_id,
      id: space.id,
      isDefault: space.is_default,
      jobRetentionTimeMinutes: space.job_retention_time_minutes,
      orchestrationBranches: space.orchestration_branches,
      repoFullName: space.repo_full_name,
      publicSchemaName: space.public_schema_name,
      repoName: space.repo_name,
      secretId: space.secret_id,
      name: spaceName,
      viewBranches: space.view_branches,
    };
  }

  update(
    orgId: OrgUIModel['id'],
    spaceId: OrgSpaceStoreModel['id'],
    settings: OrgSpaceUpdateSettings,
  ): Observable<null> {
    let params = new HttpParams();

    params = this.appendOptionalParam(params, 'is_default', settings.isDefault);
    params = this.appendOptionalParam(params, 'name', settings.name);
    params = this.appendOptionalParam(params, 'job_retention_time_minutes', settings.jobRetentionTimeMinutes);

    const orchestrationBranches = settings.orchestrationBranches ? settings.orchestrationBranches.join(',') : null;
    params = this.appendOptionalParam(params, 'orchestration_branches', orchestrationBranches);
    const viewBranches = settings.viewBranches ? settings.viewBranches.join(',') : null;
    params = this.appendOptionalParam(params, 'view_branches', viewBranches);

    return this.httpClient
      .patch<unknown>(
        `${this.baseUrl}/company/${orgId}/${spaceId}`,
        {},
        {
          params,
        },
      )
      .pipe(
        map(() => null),
        this.catchErrorAndThrow('[OrgSpaceHttpService.updateSpace]: error'),
      );
  }

  private appendOptionalParam(params: HttpParams, paramName: string, value?: unknown) {
    return isNotNullish(value) ? params.append(paramName, value as string) : params;
  }

  private mapDataWarehouseModel(dataWarehouse: DataWarehouseHttpModel): DataWarehouse | null {
    const bucket = this.mapDataWarehouseBucket(dataWarehouse.bucket);
    if (!bucket) {
      return null;
    }

    const database = this.mapDataWarehouseDatabase(dataWarehouse.database);
    if (!database) {
      return null;
    }

    return {
      bucket,
      database,
    };
  }

  private isGoogleCloudStorageDataWarehouseBucket(
    bucket: DataWarehouseBucketHttpModel,
  ): bucket is DataWarehouseGoogleCloudStorageBucketHttpModel {
    return bucket.type === BucketTypeHttpValue.GoogleCloudStorage;
  }

  private isAzureBlobStorageDataWarehouseBucket(
    bucket: DataWarehouseBucketHttpModel,
  ): bucket is DataWarehouseAzureBlobStorageBucketHttpModel {
    return bucket.type === BucketTypeHttpValue.AzureBlobStorage;
  }

  private isAmazonS3StorageDataWarehouseBucket(
    bucket: DataWarehouseBucketHttpModel,
  ): bucket is DataWarehouseAmazonS3StorageBucketHttpModel {
    return bucket.type === BucketTypeHttpValue.AmazonS3Storage;
  }

  private mapDataWarehouseBucket(bucket: DataWarehouseBucketHttpModel): DataWarehouseBucket | null {
    if (this.isGoogleCloudStorageDataWarehouseBucket(bucket)) {
      const googleCloudStorageBucket: DataWarehouseGoogleCloudStorageBucket = {
        name: bucket.name,
        projectId: bucket.project_id,
        type: BucketType.GoogleCloudStorage,
        url: buildGoogleCloudStorageUrl(bucket),
      };
      return googleCloudStorageBucket;
    }

    if (this.isAzureBlobStorageDataWarehouseBucket(bucket)) {
      const azureBlobStorageBucket: DataWarehouseAzureBlobStorageBucket = {
        accountName: bucket.account_name,
        containerName: bucket.container_name,
        subscriptionId: bucket.subscription_id,
        resourceGroup: bucket.resource_group,
        type: BucketType.AzureBlobStorage,
        url: buildAzureBlobStorageUrl(bucket),
      };
      return azureBlobStorageBucket;
    }

    if (this.isAmazonS3StorageDataWarehouseBucket(bucket)) {
      const amazonS3StorageBucket: DataWarehouseAmazonS3StorageBucket = {
        name: bucket.name,
        region: bucket.region,
        type: BucketType.AmazonS3Storage,
        url: buildAmazonS3StorageUrl(bucket),
      };
      return amazonS3StorageBucket;
    }

    this.errorHandlerService.error(`[OrgSpaceHttpService]: invalid bucket type "${bucket.type}"`);
    return null;
  }

  private isBigQueryDataWarehouseDatabase(
    database: DataWarehouseDatabaseHttpModel,
  ): database is DataWarehouseBigQueryDatabaseHttpModel {
    return database.type === DatabaseTypeHttpValue.BigQuery;
  }

  private isSnowflakeDataWarehouseDatabase(
    database: DataWarehouseDatabaseHttpModel,
  ): database is DataWarehouseSnowflakeDatabaseHttpModel {
    return database.type === DatabaseTypeHttpValue.Snowflake;
  }

  private mapDataWarehouseDatabase(database: DataWarehouseDatabaseHttpModel): DataWarehouseDatabase | null {
    if (this.isBigQueryDataWarehouseDatabase(database)) {
      const bigQueryDatabase: DataWarehouseBigQueryDatabase = {
        name: database.name,
        projectId: database.project_id,
        type: DatabaseType.BigQuery,
        url: buildBigQueryDatabaseUrl(database),
      };
      return bigQueryDatabase;
    }

    if (this.isSnowflakeDataWarehouseDatabase(database)) {
      const snowflakeDatabase: DataWarehouseSnowflakeDatabase = {
        account: database.account,
        name: database.name,
        user: database.user,
        warehouse: database.warehouse,
        type: DatabaseType.Snowflake,
        url: buildSnowflakeDatabaseUrl(database),
      };
      return snowflakeDatabase;
    }

    this.errorHandlerService.error(`[OrgSpaceHttpService]: invalid database type "${database.type}"`);
    return null;
  }

  private mapDataWarehouseConfigToHttpModel({
    googleCloudStorageConfig,
    snowflakeConfig,
    amazonS3StorageConfig,
    azureBlobStorageConfig,
  }: DataWarehouseConfig): DataWarehouseRequestHttpModel | null {
    if (googleCloudStorageConfig) {
      return this.getBigQueryGoogleCloudStorageDataWarehouseConfigHttpModel({ googleCloudStorageConfig });
    }

    if (snowflakeConfig && amazonS3StorageConfig) {
      return this.getSnowflakeAmazonS3StorageDataWarehouseConfigHttpModel({ snowflakeConfig, amazonS3StorageConfig });
    }

    if (snowflakeConfig && azureBlobStorageConfig) {
      return this.getSnowflakeAzureBlobStorageDataWarehouseHttpModel({ snowflakeConfig, azureBlobStorageConfig });
    }

    return null;
  }

  private getBigQueryGoogleCloudStorageDataWarehouseConfigHttpModel({
    googleCloudStorageConfig,
  }: {
    googleCloudStorageConfig: GoogleCloudStorageConfiguration;
  }): BigQueryGoogleCloudStorageDataWarehouseRequestHttpModel {
    return {
      secret: googleCloudStorageConfig.serviceAccount,
      location: googleCloudStorageConfig.location,
    };
  }

  private getSnowflakeAmazonS3StorageDataWarehouseConfigHttpModel({
    snowflakeConfig,
    amazonS3StorageConfig,
  }: {
    snowflakeConfig: SnowflakeConfiguration;
    amazonS3StorageConfig: AmazonS3StorageConfiguration;
  }): SnowflakeAmazonS3StorageDataWarehouseRequestHttpModel {
    return {
      amazon_s3: {
        access_key_id: amazonS3StorageConfig.accessKeyId,
        region: amazonS3StorageConfig.region,
        secret_access_key: amazonS3StorageConfig.secretAccessKey,
      },
      user: snowflakeConfig.user,
      password: snowflakeConfig.password,
      account: snowflakeConfig.account,
      warehouse: snowflakeConfig.warehouse,
      database: snowflakeConfig.database,
    };
  }

  private getSnowflakeAzureBlobStorageDataWarehouseHttpModel({
    snowflakeConfig,
    azureBlobStorageConfig,
  }: {
    snowflakeConfig: SnowflakeConfiguration;
    azureBlobStorageConfig: AzureBlobStorageConfiguration;
  }): SnowflakeAzureBlobStorageDataWarehouseRequestHttpModel {
    return {
      azure_creds: {
        account_key: azureBlobStorageConfig.accountKey,
        account_name: azureBlobStorageConfig.accountName,
        container_name: azureBlobStorageConfig.containerName,
      },
      user: snowflakeConfig.user,
      password: snowflakeConfig.password,
      account: snowflakeConfig.account,
      warehouse: snowflakeConfig.warehouse,
      database: snowflakeConfig.database,
    };
  }

  private catchErrorAndThrow<T, O extends ObservableInput<never>>(
    message: string,
  ): OperatorFunction<T, T | ObservedValueOf<O>> {
    return this.errorHandlerService.catchError(message, (error: unknown) =>
      throwError(() => {
        if (error instanceof HttpErrorResponse) {
          return new Error((error.error as ErrorResponseHttpModel)?.error_message || '');
        }
        return error;
      }),
    );
  }
}
